/**
 * Kono-internal: drafts for items we have no known vendor for, then send
 * reject / ask-more-time / we-will-find to the customer.
 */
import { useEffect, useState } from "react";
import {
  UnknownItemDrafts,
  fetchUnknownItemDrafts,
  messageOf,
  sendUnknownItemDecision,
} from "./api";

const ACTIONS: {
  id: keyof UnknownItemDrafts;
  label: string;
}[] = [
  { id: "reject", label: "Reject / apologize" },
  { id: "ask-more-time", label: "Ask for more time" },
  { id: "promise-find", label: "Promise we will find them" },
];

type Props = {
  rfqId: string;
  onSent: () => void;
  onError: (message: string) => void;
};

export default function RfqUnknownItemPanel({
  rfqId,
  onSent,
  onError,
}: Props) {
  const [drafts, setDrafts] = useState<UnknownItemDrafts | null>(null);
  const [action, setAction] = useState<keyof UnknownItemDrafts>("reject");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const result = await fetchUnknownItemDrafts(rfqId);
      setDrafts(result.drafts);
      const current = result.drafts[action];
      setSubject(current.subject);
      setBody(current.body);
    } catch (e) {
      onError(messageOf(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfqId]);

  const select = (next: keyof UnknownItemDrafts) => {
    setAction(next);
    if (drafts) {
      setSubject(drafts[next].subject);
      setBody(drafts[next].body);
    }
  };

  const send = async () => {
    if (
      !window.confirm(
        "Send this email to the customer now? You can still edit it first.",
      )
    ) {
      return;
    }
    setSending(true);
    try {
      await sendUnknownItemDecision(rfqId, { action, subject, body });
      onSent();
    } catch (e) {
      onError(messageOf(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mt-4 rounded-xl border border-amber-200 bg-amber-50/50 p-4">
      <h3 className="text-sm font-semibold text-[#00062e]">
        Unknown items{" "}
        <span className="ml-1 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-700">
          Kono internal
        </span>
      </h3>
      <p className="mt-0.5 text-xs text-gray-600">
        We have no known vendor for some lines. Draft an email — reject,
        ask for more time, or promise we will find them — then send it.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {ACTIONS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => select(item.id)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
              action === item.id
                ? "border-teal-500 bg-white text-teal-800"
                : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            {item.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? "Drafting…" : "Refresh drafts"}
        </button>
      </div>
      {drafts && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
          <label className="block text-xs font-medium text-gray-500">
            Subject
          </label>
          <input
            className="mt-0.5 w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <label className="mt-2 block text-xs font-medium text-gray-500">
            Body
          </label>
          <textarea
            className="mt-0.5 w-full rounded-lg border border-gray-300 px-2.5 py-1.5 font-sans text-sm"
            rows={10}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <button
            type="button"
            onClick={() => void send()}
            disabled={sending || !subject.trim() || !body.trim()}
            className="mt-2 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send to customer"}
          </button>
        </div>
      )}
    </section>
  );
}
